/* Context module: hook + provider. */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, apiDeleteJson, apiGet, apiPatch, apiPost, apiPut } from '@/api/client';
import { jwtNeedsRefresh } from '@/auth/jwtExpiry';
import { hasPermission, type PwaSessionMe } from '@/auth/permissions';
import { PWA_JWT_STORAGE_KEY, PWA_OPAQUE_STORAGE_KEY } from '@/auth/storageKeys';

const OPAQUE_KEY = PWA_OPAQUE_STORAGE_KEY;
const JWT_KEY = PWA_JWT_STORAGE_KEY;

export type PwaSessionData = {
  token: string;
  expiresInSec: number;
  organizationId: string;
  user: { id: string; email: string };
};

type Ctx = {
  jwt: string | null;
  opaque: string | null;
  /** Restaurando sesión desde token opaco guardado. */
  bootstrapping: boolean;
  userEmail: string | null;
  /** Nombre del usuario o email si no hay nombre. */
  userDisplayName: string | null;
  /** Nombre de la organización (empresa) de la sesión. */
  organizationName: string | null;
  /** Logo de marca de la empresa (URL absoluta o relativa al API). */
  organizationLogoUrl: string | null;
  permissions: Set<string>;
  /** true tras el primer `/api/console/me` exitoso de la sesión */
  permissionsLoaded: boolean;
  can: (required: string | string[]) => boolean;
  exchange: (opaqueToken: string) => Promise<void>;
  logout: () => void;
  get: <T>(path: string, init?: RequestInit) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  patch: <T>(path: string, body?: unknown) => Promise<T>;
  deleteJson: <T>(path: string, body?: unknown) => Promise<T>;
};

const SessionContext = createContext<Ctx | null>(null);

async function fetchMe(token: string): Promise<PwaSessionMe> {
  return apiGet<PwaSessionMe>('/api/console/me', token);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [jwt, setJwt] = useState<string | null>(() => localStorage.getItem(JWT_KEY));
  const [opaque, setOpaque] = useState<string | null>(() => localStorage.getItem(OPAQUE_KEY));
  const [bootstrapping, setBootstrapping] = useState(() => {
    const op = localStorage.getItem(OPAQUE_KEY);
    const j = localStorage.getItem(JWT_KEY);
    return Boolean(op && jwtNeedsRefresh(j));
  });
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [organizationLogoUrl, setOrganizationLogoUrl] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Set<string>>(() => new Set());
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const exchange = useCallback(async (opaqueToken: string) => {
    const d = await apiPost<PwaSessionData>('/api/pwa/session', { token: opaqueToken }, null);
    localStorage.setItem(OPAQUE_KEY, opaqueToken);
    localStorage.setItem(JWT_KEY, d.token);
    setOpaque(opaqueToken);
    setJwt(d.token);
    setUserEmail(d.user.email);
    setUserDisplayName(d.user.email);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(JWT_KEY);
    localStorage.removeItem(OPAQUE_KEY);
    setJwt(null);
    setOpaque(null);
    setUserEmail(null);
    setUserDisplayName(null);
    setOrganizationName(null);
    setOrganizationLogoUrl(null);
    setPermissions(new Set());
    setPermissionsLoaded(false);
  }, []);

  const refreshJwt = useCallback(async () => {
    const op = opaque ?? localStorage.getItem(OPAQUE_KEY);
    if (!op) {
      logout();
      throw new ApiError('Sin token de acceso', 401);
    }
    try {
      await exchange(op);
    } catch (e) {
      logout();
      throw e;
    }
  }, [exchange, opaque, logout]);

  const wrap = useCallback(
    async <T,>(fn: (token: string) => Promise<T>): Promise<T> => {
      let token = jwt ?? localStorage.getItem(JWT_KEY);
      if (!token) {
        await refreshJwt();
        token = localStorage.getItem(JWT_KEY);
      }
      if (!token) throw new ApiError('Sesión requerida', 401);
      try {
        return await fn(token);
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          try {
            await refreshJwt();
            token = localStorage.getItem(JWT_KEY);
            if (!token) throw e;
            return await fn(token);
          } catch (retryErr) {
            logout();
            throw retryErr;
          }
        }
        throw e;
      }
    },
    [jwt, refreshJwt, logout]
  );

  const get = useCallback(
    <T,>(path: string, init?: RequestInit) => wrap<T>((t) => apiGet<T>(path, t, init)),
    [wrap]
  );
  const post = useCallback(<T,>(path: string, body?: unknown) => wrap<T>((t) => apiPost<T>(path, body, t)), [wrap]);
  const put = useCallback(<T,>(path: string, body?: unknown) => wrap<T>((t) => apiPut<T>(path, body, t)), [wrap]);
  const patch = useCallback(<T,>(path: string, body?: unknown) => wrap<T>((t) => apiPatch<T>(path, body, t)), [wrap]);
  const deleteJson = useCallback(
    <T,>(path: string, body?: unknown) => wrap<T>((t) => apiDeleteJson<T>(path, body, t)),
    [wrap]
  );

  const applyMe = useCallback((me: PwaSessionMe) => {
    const email = typeof me.user?.email === 'string' ? me.user.email : null;
    const name = typeof me.user?.name === 'string' ? me.user.name.trim() : '';
    const orgName =
      typeof me.organization?.name === 'string' ? me.organization.name.trim() : '';
    const logo =
      typeof me.organization?.logoUrl === 'string' ? me.organization.logoUrl.trim() : '';
    setUserEmail(email);
    setUserDisplayName(name || email);
    setOrganizationName(orgName || null);
    setOrganizationLogoUrl(logo || null);
    setPermissions(new Set(me.permissions ?? []));
    setPermissionsLoaded(true);
  }, []);

  const loadMeWithRefresh = useCallback(async () => {
    let token = localStorage.getItem(JWT_KEY);
    if (!token) {
      await refreshJwt();
      token = localStorage.getItem(JWT_KEY);
    }
    if (!token) return;
    try {
      applyMe(await fetchMe(token));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        await refreshJwt();
        token = localStorage.getItem(JWT_KEY);
        if (!token) throw e;
        applyMe(await fetchMe(token));
        return;
      }
      throw e;
    }
  }, [applyMe, refreshJwt]);

  /** Renueva JWT al abrir la app si el opaco sigue guardado (evita pedir el token otra vez). */
  useEffect(() => {
    const op = localStorage.getItem(OPAQUE_KEY);
    if (!op) {
      setBootstrapping(false);
      return;
    }
    const currentJwt = localStorage.getItem(JWT_KEY);
    if (!jwtNeedsRefresh(currentJwt)) {
      setBootstrapping(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await exchange(op);
      } catch {
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exchange, logout]);

  useEffect(() => {
    if (bootstrapping || !jwt) return;
    let cancelled = false;
    const boot = window.setTimeout(() => {
      void loadMeWithRefresh()
        .then(() => {
          if (cancelled) return;
        })
        .catch((e) => {
          if (!cancelled && e instanceof ApiError && e.status === 401) logout();
        });
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(boot);
    };
  }, [jwt, bootstrapping, loadMeWithRefresh, logout]);

  /** Comprueba revocación del token opaco aunque el JWT siga vigente. */
  useEffect(() => {
    if (bootstrapping || !jwt) return;
    const probe = () => {
      void loadMeWithRefresh().catch((e) => {
        if (e instanceof ApiError && e.status === 401) logout();
      });
    };
    const intervalId = window.setInterval(probe, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') probe();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [jwt, bootstrapping, loadMeWithRefresh, logout]);

  const can = useCallback(
    (required: string | string[]) => hasPermission(permissions, required),
    [permissions]
  );

  const value = useMemo(
    () => ({
      jwt,
      opaque,
      bootstrapping,
      userEmail,
      userDisplayName,
      organizationName,
      organizationLogoUrl,
      permissions,
      permissionsLoaded,
      can,
      exchange,
      logout,
      get,
      post,
      put,
      patch,
      deleteJson,
    }),
    [
      jwt,
      opaque,
      bootstrapping,
      userEmail,
      userDisplayName,
      organizationName,
      organizationLogoUrl,
      permissions,
      permissionsLoaded,
      can,
      exchange,
      logout,
      get,
      post,
      put,
      patch,
      deleteJson,
    ]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Ctx {
  const c = useContext(SessionContext);
  if (!c) throw new Error('useSession fuera de SessionProvider');
  return c;
}
